import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignInAlt, faStethoscope, faTooth, faHeartPulse, faUserDoctor, faSyringe, faNotesMedical } from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../ui/BtnTaratic';
import { ES as FlagES, GB as FlagGB } from 'country-flag-icons/react/3x2';
import { SlidingPills } from '../ui/SlidingPills';

export const PublicLayout: React.FC = () => {
  const { t, i18n } = useTranslation();

  const currentLang = i18n.language?.startsWith('es') ? 'es' : 'en';

  const handleLanguageChange = (id: string) => {
    const newLang = id as 'es' | 'en';
    i18n.changeLanguage(newLang);
    try {
      localStorage.setItem('ui_language', newLang);
    } catch { }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col font-sans relative">
      {/* Background glow effects & Floating Icons - Isolated overflow to preserve sticky header */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-72 sm:w-96 h-72 sm:h-96 bg-blue-500/10 rounded-full blur-[100px] sm:blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-80 sm:w-[30rem] h-80 sm:h-[30rem] bg-purple-500/5 rounded-full blur-[100px] sm:blur-[120px]" />

        {/* Floating Medical Icons - Scaled for mobile */}
        <FontAwesomeIcon icon={faStethoscope} className="absolute top-[18%] left-[4%] sm:left-[8%] text-[5rem] sm:text-[9rem] text-blue-500/10 -rotate-12 animate-pulse" />
        <FontAwesomeIcon icon={faTooth} className="absolute top-[12%] right-[5%] sm:right-[10%] text-[6rem] sm:text-[10rem] text-blue-500/10 rotate-12" />
        <FontAwesomeIcon icon={faHeartPulse} className="absolute top-[48%] left-[2%] sm:left-[3%] text-[7rem] sm:text-[12rem] text-purple-500/10 -rotate-6" />
        <FontAwesomeIcon icon={faUserDoctor} className="absolute top-[58%] right-[4%] sm:right-[6%] text-[5rem] sm:text-[9rem] text-blue-500/10 rotate-6 animate-pulse" />
        <FontAwesomeIcon icon={faSyringe} className="absolute bottom-[18%] left-[10%] sm:left-[18%] text-[4rem] sm:text-[8rem] text-blue-500/10 rotate-45" />
        <FontAwesomeIcon icon={faNotesMedical} className="absolute bottom-[8%] right-[10%] sm:right-[20%] text-[6rem] sm:text-[11rem] text-purple-500/10 -rotate-12" />
      </div>

      {/* Header / Navbar */}
      <header className="w-full bg-white/20 backdrop-blur-[40px] z-50 sticky top-0 flex flex-col shadow-[0_4px_30px_rgba(0,0,0,0.03)]">
        {/* Top Contact Bar */}
        <div className="w-full bg-transparent py-1.5 border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-between sm:justify-end items-center gap-2 sm:gap-4 text-[10px] sm:text-[11px] font-medium text-gray-700 tracking-wider">
            <a href="mailto:contacto@taratic.com" className="hover:text-blue-600 transition-colors truncate max-w-[150px] sm:max-w-none">
              contacto@taratic.com
            </a>
            <span className="opacity-30">|</span>
            <div className="flex items-center gap-1">
              <span>🇪🇸</span>
              <a href="tel:+34684788269" className="hover:text-gray-900 transition-colors">
                (+34) 684 788 269
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-3 sm:px-6 min-h-14 py-2 sm:py-0 sm:h-16 w-full flex flex-wrap items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-1 sm:gap-1.5 shrink-0">
            <img
              src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_logo.svg"
              alt="Taratic Logo"
              className="h-8 sm:h-10 w-auto"
            />
            <img
              src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_letras.svg"
              alt="TARATIC"
              className="h-3.5 sm:h-5 w-auto hidden min-[400px]:block"
            />
            <span className="text-base sm:text-xl font-bold italic text-blue-600 hover:text-blue-600 tracking-wide ml-0 relative top-[0.8px] inline-block [text-shadow:_0_0_4px_rgba(255,255,255,0.8),_0_0_8px_rgba(255,255,255,0.4)]">
              {t('legal.clinicsTag')}
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-6 md:gap-8">
            <Link to="/login">
              <BtnTaratic color="blue" className="px-3.5 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm">
                <span className="font-medium">{t('legal.login')}</span>
                <FontAwesomeIcon icon={faSignInAlt} className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1.5" />
              </BtnTaratic>
            </Link>

            <div className="-mr-2 sm:-mr-4">
              <SlidingPills
                options={[
                  {
                    id: 'es',
                    label: 'ES',
                    customIcon: <FlagES className="w-3.5 h-2.5 sm:w-4 sm:h-3 rounded-sm shadow-sm" />
                  },
                  {
                    id: 'en',
                    label: 'EN',
                    customIcon: <FlagGB className="w-3.5 h-2.5 sm:w-4 sm:h-3 rounded-sm shadow-sm" />
                  }
                ]}
                activeId={currentLang}
                onChange={handleLanguageChange}
                size="xs"
                variant="light"
                tabMinWidth="38px"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 relative z-10 flex flex-col">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200 py-4 sm:py-8 mt-auto z-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-6 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-gray-500 text-center md:text-left">
          <p>
            &copy; {new Date().getFullYear()} TARATIC. {t('legal.rightsReserved')}
          </p>
          <div className="flex flex-wrap justify-center md:justify-end gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600">
            <Link to="/privacy" className="hover:text-blue-600 transition-colors">{t('legal.privacy')}</Link>
            <Link to="/terms" className="hover:text-blue-600 transition-colors">{t('legal.terms')}</Link>
            <Link to="/data-deletion/users" className="hover:text-blue-600 transition-colors">{t('legal.dataDeletionUsers')}</Link>
            <Link to="/data-deletion/clinics" className="hover:text-blue-600 transition-colors">{t('legal.dataDeletionClinics')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
